/**
 * Angebotsliste / Angebotsanfrage
 *
 * Der Store haelt bewusst nur handle, variantId und Menge. Preise, Bild, SKU
 * und EAN kommen bei jedem Aufbau frisch von
 * /products/{handle}?view=angebot-json, sonst stuende im Angebot irgendwann ein
 * Preis von vorletzter Woche.
 *
 * Bestandsdaten spielen hier keine Rolle: Mengen sind nirgends gedeckelt und
 * der Lagerstand wird auch nicht angezeigt. Eine Anfrage darf ueber den
 * Bestand hinausgehen, nachbestellt wird dann fuer die Anfrage.
 */
(() => {
  const STORAGE_KEY = 'mdzo-angebot-v1';
  const SUBMIT_KEY = 'mdzo-angebot-submitted';
  const ORIGIN_KEY = 'mdzo-angebot-origin';
  const CHANGE_EVENT = 'mdzo-angebot:change';
  const MAX_ITEMS = 50;
  const SUGGEST_LIMIT = 6;
  const MIN_SEARCH_CHARS = 2;
  const SEARCH_DEBOUNCE_MS = 250;

  // Shopifys Bot-Schutz haengt am selben submit-Event und bricht ab, solange
  // sein Captcha-Skript nicht geladen ist -- lautlos, ohne Fehler und ohne
  // spaeteren zweiten Versuch. Geladen wird erst beim ersten focusin oder
  // change im Formular, wer also die Pflicht-Checkbox anklickt und sofort
  // absendet, verliert den Klick. So lange warten wir auf die Bindung.
  const CAPTCHA_WAIT_MS = 8000;
  const CAPTCHA_POLL_MS = 150;

  // Nach 30 Tagen ohne Aenderung gilt die Liste als verfallen. Ohne Ablauf
  // stuende Monate spaeter ein Angebot mit Positionen im Header, an die sich
  // niemand mehr erinnert. Jede Aenderung setzt die Frist neu.
  const TTL_MS = 30 * 24 * 60 * 60 * 1000;

  // Auf der Seite gilt shop.money_format wie im uebrigen Theme, in der Mail
  // dagegen die Variante mit Waehrung. Beide reicht die Section per data-Attribut
  // durch, das Theme-Global kennt nur die zweite.
  const formats = {
    display: '{{ amount_with_comma_separator }} €',
    currency: (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat)
      || '{{ amount_with_comma_separator }} EUR'
  };

  /**
   * Formats an amount in cents using a Shopify money format string.
   * @param {number} cents - Amount in cents.
   * @param {string} [format] - Format string, defaults to the display format.
   * @returns {string} Formatted amount.
   */
  function formatMoney(cents, format) {
    const moneyFormat = format || formats.display;
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = moneyFormat.match(placeholderRegex);
    const placeholder = match ? match[1] : 'amount';
    const withComma = placeholder.indexOf('comma_separator') !== -1;
    const noDecimals = placeholder.indexOf('no_decimals') !== -1;
    const thousands = withComma ? '.' : ',';
    const decimal = withComma ? ',' : '.';

    const num = (Number(cents) / 100).toFixed(noDecimals ? 0 : 2);
    const parts = num.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const rest = parts[1] ? decimal + parts[1] : '';

    return moneyFormat.replace(placeholderRegex, whole + rest);
  }

  /**
   * Whether net prices currently lead, following the theme wide switch from
   * assets/md-price-mode.js. Zeilen- und Summenbetraege blenden rein per CSS
   * um; gefragt wird das hier nur fuer Betraege, die als Zahl in einem Satz
   * stehen und deshalb umgerechnet werden muessen.
   * @returns {boolean} True when net leads.
   */
  function netLeads() {
    return !!(window.mdPrice && window.mdPrice.enabled && window.mdPrice.mode === 'net');
  }

  /**
   * Derives the net amount from a gross amount.
   * Mirrors snippets/md-net-price.liquid so both never drift apart.
   * @param {number} grossCents - Gross amount in cents.
   * @param {number} rate - Tax rate in percent.
   * @returns {number} Net amount in cents.
   */
  function toNet(grossCents, rate) {
    const taxRate = Number(rate);
    if (!taxRate || taxRate <= 0) return Number(grossCents);
    return Math.round(Number(grossCents) / (1 + taxRate / 100));
  }

  /* ------------------------------------------------------------------ store */

  /**
   * Reads the stored quote list, tolerating corrupted or foreign data.
   * @returns {Array<object>} Stored items.
   */
  function read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed.items)) return [];

      if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
        window.localStorage.removeItem(STORAGE_KEY);
        return [];
      }

      return parsed.items.filter((item) => item && item.handle && item.variantId);
    } catch (e) {
      return [];
    }
  }

  /**
   * Persists the quote list and notifies listeners.
   * @param {Array<object>} items - Items to store.
   */
  function write(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        items,
        updatedAt: Date.now(),
        expiresAt: Date.now() + TTL_MS
      }));
    } catch (e) {
      // Private mode or full storage: the list then only lives for this page.
    }

    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      detail: { items, count: items.length }
    }));
  }

  const store = {
    read,

    /**
     * @returns {number} Number of positions in the list.
     */
    count() {
      return read().length;
    },

    /**
     * @param {string|number} variantId - Variant id to look for.
     * @returns {boolean} Whether the variant is already listed.
     */
    has(variantId) {
      const id = String(variantId);
      return read().some((item) => String(item.variantId) === id);
    },

    /**
     * Adds a variant, merging the quantity if it is already listed.
     * @param {string} handle - Product handle.
     * @param {string|number} variantId - Variant id.
     * @param {number} [qty] - Quantity to add.
     * @returns {boolean} False if the list is full.
     */
    add(handle, variantId, qty) {
      const items = read();
      const id = String(variantId);
      const amount = Math.max(1, parseInt(qty, 10) || 1);
      const existing = items.find((item) => String(item.variantId) === id);

      if (existing) {
        existing.qty = Math.max(1, existing.qty + amount);
      } else {
        if (items.length >= MAX_ITEMS) return false;
        items.push({ handle, variantId: id, qty: amount });
      }

      write(items);
      return true;
    },

    /**
     * @param {string|number} variantId - Variant id.
     * @param {number} qty - New quantity.
     */
    setQty(variantId, qty) {
      const items = read();
      const id = String(variantId);
      const item = items.find((entry) => String(entry.variantId) === id);
      if (!item) return;

      item.qty = Math.max(1, parseInt(qty, 10) || 1);
      write(items);
    },

    /**
     * Swaps a position onto another variant of the same product, keeping its
     * place in the list. Liegt die Zielvariante schon in der Liste, werden die
     * Mengen zusammengefuehrt statt eine zweite Zeile anzulegen.
     * @param {string|number} fromId - Currently listed variant id.
     * @param {string|number} toId - Variant id to switch to.
     */
    setVariant(fromId, toId) {
      const from = String(fromId);
      const to = String(toId);
      if (from === to) return;

      const items = read();
      const index = items.findIndex((item) => String(item.variantId) === from);
      if (index === -1) return;

      const existing = items.findIndex((item) => String(item.variantId) === to);

      if (existing !== -1) {
        items[existing].qty = Math.max(1, items[existing].qty + items[index].qty);
        items.splice(index, 1);
      } else {
        items[index].variantId = to;
      }

      write(items);
    },

    /**
     * @param {string|number} variantId - Variant id to remove.
     */
    remove(variantId) {
      const id = String(variantId);
      write(read().filter((item) => String(item.variantId) !== id));
    },

    clear() {
      write([]);
    }
  };

  /* ------------------------------------------------------------ product data */

  const productCache = new Map();

  /**
   * Loads live product data through the angebot-json view template.
   * @param {string} handle - Product handle.
   * @returns {Promise<object>} Product data.
   */
  function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    const rootUrl = (window.theme && theme.routes && theme.routes.cart)
      ? theme.routes.cart.replace(/\/cart$/, '')
      : '';
    const request = fetch(`${rootUrl}/products/${encodeURIComponent(handle)}?view=angebot-json`)
      .then((response) => {
        if (!response.ok) throw new Error(`Produkt ${handle} nicht erreichbar`);
        return response.json();
      })
      .catch((error) => {
        productCache.delete(handle);
        throw error;
      });

    productCache.set(handle, request);
    return request;
  }

  /**
   * Resolves the stored items into rows with live prices.
   * Items whose product or variant no longer exists are dropped.
   * @returns {Promise<Array<object>>} Resolved rows.
   */
  async function resolveItems() {
    const items = read();
    if (!items.length) return [];

    const results = await Promise.all(items.map(async (item) => {
      try {
        const product = await fetchProduct(item.handle);
        const variant = product.variants.find(
          (entry) => String(entry.variant_id) === String(item.variantId)
        );
        if (!variant) return null;

        // variant.price ist der Preis bei Menge 1. Die Mengenstaffel aus
        // snippets/md-volume-discount.liquid wird hier bewusst nicht
        // angewendet: das individuelle Angebot tritt an ihre Stelle, sonst
        // wuerde zweimal rabattiert.
        const grossUnit = Number(variant.price);
        const grossTotal = grossUnit * item.qty;

        return {
          handle: item.handle,
          qty: item.qty,
          productId: product.product_id,
          productTitle: product.title,
          taxRate: product.tax_rate,
          variantId: variant.variant_id,
          variantTitle: variant.title,
          // Fuer den Variantenwechsel direkt in der Zeile: der Endpoint
          // liefert ohnehin alle Varianten, ein zweiter Request entfaellt.
          variants: product.variants.map((entry) => ({
            id: entry.variant_id,
            title: entry.title
          })),
          sku: variant.sku || '',
          barcode: variant.barcode || '',
          image: variant.image || '',
          url: variant.url,
          absoluteUrl: variant.absolute_url || variant.url,
          grossUnit,
          grossTotal,
          netUnit: toNet(grossUnit, product.tax_rate),
          netTotal: toNet(grossTotal, product.tax_rate)
        };
      } catch (e) {
        return null;
      }
    }));

    return results.filter(Boolean);
  }

  /**
   * @param {number} cents - Amount in cents.
   * @returns {string} Amount including the currency code, for the email.
   */
  function formatMoneyWithCurrency(cents) {
    return formatMoney(cents, formats.currency);
  }

  window.mdzoAngebot = {
    store,
    fetchProduct,
    resolveItems,
    formats,
    formatMoney,
    formatMoneyWithCurrency,
    toNet,
    CHANGE_EVENT,
    STORAGE_KEY,
    SUBMIT_KEY
  };

  /**
   * Opens a modal, falling back to the open attribute if main.js has not
   * upgraded the element yet. The custom element picks up its own click
   * handler on upgrade, so closing keeps working either way.
   * @param {Element} modal - Modal element.
   * @param {Element} opener - Element that focus returns to.
   */
  function openModal(modal, opener) {
    if (typeof modal.open === 'function') {
      modal.open(opener);
    } else {
      modal.setAttribute('open', '');
    }
  }

  /* -------------------------------------------------------------- PDP button */

  class MdzoAngebotButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-mdzo-add]');
      if (!this.button) return;

      this.labelEl = this.button.querySelector('[data-mdzo-label]') || this.button;
      this.feedbackEl = this.querySelector('[data-mdzo-feedback]');
      this.listLink = this.querySelector('[data-mdzo-list-link]');
      this.variantId = this.dataset.variantId || '';
      this.handle = this.dataset.handle;
      this.addedModal = this.relocateModal();

      this.button.addEventListener('click', this.handleClick.bind(this));
      document.addEventListener(CHANGE_EVENT, this.syncLabel.bind(this));

      // The variant picker sits in another column, the event bubbles up to .product.
      const scope = this.closest('.product') || this.closest('.shopify-section');
      if (scope) scope.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.syncLabel();
    }

    /**
     * Keeps the tracked variant in sync with the product form.
     * @param {CustomEvent} evt - Variant change event.
     */
    handleVariantChange(evt) {
      if (!evt.detail || !evt.detail.variant) return;
      this.variantId = String(evt.detail.variant.id);
      this.clearFeedback();
    }

    /**
     * Switches between the first-item and add-to-existing-list wording.
     */
    syncLabel() {
      const hasList = store.count() > 0;
      const label = hasList ? this.dataset.labelAdd : this.dataset.labelRequest;
      if (label) this.labelEl.textContent = label;
      if (this.listLink) this.listLink.hidden = !hasList;
    }

    clearFeedback() {
      if (!this.feedbackEl) return;
      this.feedbackEl.hidden = true;
      this.feedbackEl.textContent = '';
    }

    /**
     * Reads the quantity from the product form, without any stock ceiling.
     * @returns {number} Requested quantity.
     */
    getQuantity() {
      const scope = this.closest('.product') || document;
      // 22.09.2026: Das Mengenfeld kann ausserhalb des Formulars stehen (Rabattkarte) und haengt dann per
      // form-Attribut daran -- form.elements kennt es, ein Nachfahren-Selektor nicht.
      const form = scope.querySelector('.js-product-form-main') || scope.querySelector('form[action*="/cart/add"]');
      const input = (form && form.elements && form.elements.namedItem('quantity'))
        || scope.querySelector('.js-product-form-main input[name="quantity"]')
        || scope.querySelector('form[action*="/cart/add"] input[name="quantity"]');
      const qty = input ? parseInt(input.value, 10) : 1;
      return Math.max(1, qty || 1);
    }

    handleClick() {
      if (!this.variantId || !this.handle) return;

      if (!store.add(this.handle, this.variantId, this.getQuantity())) {
        if (!this.feedbackEl) return;
        this.feedbackEl.hidden = false;
        this.feedbackEl.textContent = this.dataset.feedbackFull || '';
        this.feedbackEl.classList.add('mdzo-angebot-cta__feedback--error');
        return;
      }

      this.clearFeedback();
      this.rememberOrigin();
      this.showAddedModal();
    }

    /**
     * Notes which product the list was filled from, so the list page can offer
     * a way back. sessionStorage statt localStorage: "wo ich herkam" gehoert
     * zur laufenden Sitzung und soll nicht Wochen spaeter noch gelten.
     */
    rememberOrigin() {
      try {
        window.sessionStorage.setItem(ORIGIN_KEY, JSON.stringify({
          title: this.dataset.productTitle || '',
          url: this.dataset.productUrl || window.location.pathname
        }));
      } catch (e) {
        // Ohne sessionStorage fehlt auf der Listenseite nur der Zurueck-Button.
      }
    }

    /**
     * Hebt das Bestaetigungsmodal aus der Buy-Column heraus direkt unter body.
     * Dort stehen Nachbarn wie .product-info__sticky, und sticky oeffnet einen
     * eigenen Stacking Context. Darin eingesperrt legt sich das Overlay nur
     * ueber einen Teil der Seite: alles, was nach dem Produktbereich kommt,
     * bleibt unabgedunkelt darueber liegen.
     * @returns {?Element} Das Modal an seinem neuen Platz.
     */
    relocateModal() {
      const modal = this.querySelector('[data-mdzo-added-modal]');
      if (!modal) return this.addedModal || null;

      // Ein Section-Rerender liefert eine frische Kopie mit; die alte muss weg,
      // sonst gibt es die id doppelt.
      document.querySelectorAll('body > [data-mdzo-added-modal]').forEach((stale) => {
        if (stale.id === modal.id) stale.remove();
      });

      document.body.appendChild(modal);
      return modal;
    }

    /**
     * Confirms the addition and offers the choice between staying here and
     * going to the list.
     */
    showAddedModal() {
      const modal = this.addedModal;
      if (!modal) return;

      const count = store.count();

      const textEl = modal.querySelector('[data-mdzo-added-text]');
      if (textEl) {
        textEl.textContent = (this.dataset.modalText || '')
          .replace('[title]', this.dataset.productTitle || '');
      }

      const countEl = modal.querySelector('[data-mdzo-added-count]');
      if (countEl) {
        const template = count === 1 ? this.dataset.modalCountOne : this.dataset.modalCountMany;
        countEl.textContent = (template || '').replace('[count]', String(count));
      }

      openModal(modal, this.button);
    }
  }

  if (!customElements.get('mdzo-angebot-button')) {
    customElements.define('mdzo-angebot-button', MdzoAngebotButton);
  }

  /* ---------------------------------------------------------------- list page */

  class MdzoAngebotListe extends HTMLElement {
    connectedCallback() {
      if (this.dataset.moneyFormat) formats.display = this.dataset.moneyFormat;
      if (this.dataset.moneyCurrencyFormat) formats.currency = this.dataset.moneyCurrencyFormat;

      this.minNet = parseInt(this.dataset.minNet, 10) || 0;
      this.fallbackRate = parseFloat(this.dataset.fallbackTaxRate) || 0;
      this.rowTemplate = this.querySelector('[data-mdzo-row-template]');
      this.itemsEl = this.querySelector('[data-mdzo-items]');
      this.emptyEl = this.querySelector('[data-mdzo-empty]');
      this.tableEl = this.querySelector('[data-mdzo-table]');
      this.summaryEl = this.querySelector('[data-mdzo-summary]');
      this.contentEl = this.querySelector('[data-mdzo-content]');
      this.netTotalEl = this.querySelector('[data-mdzo-net-total]');
      this.grossTotalEl = this.querySelector('[data-mdzo-gross-total]');
      this.thresholdEl = this.querySelector('[data-mdzo-threshold]');
      this.thresholdBox = this.querySelector('[data-mdzo-threshold-box]');
      this.badgeEl = this.querySelector('[data-mdzo-badge]');
      this.submitBtn = this.querySelector('[data-mdzo-submit]');
      this.submitLabelEl = this.querySelector('[data-mdzo-submit-label]');
      this.submitStatusEl = this.querySelector('[data-mdzo-submit-status]');
      this.form = this.querySelector('form.mdzo-angebot__form');
      this.positionsEl = this.querySelector('[data-mdzo-positions]');
      this.skuInput = this.querySelector('[data-mdzo-sku-input]');
      this.skuButton = this.querySelector('[data-mdzo-sku-submit]');
      this.skuFeedback = this.querySelector('[data-mdzo-sku-feedback]');
      this.suggestEl = this.querySelector('[data-mdzo-suggest]');
      this.suggestTemplate = this.querySelector('[data-mdzo-suggest-template]');

      this.rows = [];
      this.entries = [];
      this.signature = null;
      this.renderToken = 0;
      this.searchToken = 0;
      this.searchTimer = 0;

      this.bindEvents();
      this.showOrigin();
      this.handleSuccess();
      this.render();
    }

    bindEvents() {
      document.addEventListener(CHANGE_EVENT, () => this.render());

      // Betraege in Zeilen und Summe blendet CSS um. Der Fehlbetrag steht
      // dagegen als Zahl in einem Satz und im Buttontext, der muss neu
      // gerechnet werden.
      document.addEventListener('md:pricemode', () => this.updateSummary(this.rows));

      if (this.itemsEl) {
        this.itemsEl.addEventListener('click', this.handleItemsClick.bind(this));
        this.itemsEl.addEventListener('change', this.handleQtyChange.bind(this));
        this.itemsEl.addEventListener('input', this.handleQtyChange.bind(this));
        this.itemsEl.addEventListener('change', this.handleVariantSwitch.bind(this));
      }

      if (this.skuButton) {
        this.skuButton.addEventListener('click', () => this.addEntry(0));
      }

      if (this.skuInput) {
        this.skuInput.addEventListener('input', () => {
          window.clearTimeout(this.searchTimer);
          this.searchTimer = window.setTimeout(() => this.loadSuggestions(), SEARCH_DEBOUNCE_MS);
        });

        this.skuInput.addEventListener('keydown', (evt) => {
          if (evt.key === 'Escape') {
            this.closeSuggestions();
            return;
          }

          if (evt.key === 'ArrowDown' && this.suggestEl && !this.suggestEl.hidden) {
            evt.preventDefault();
            const first = this.suggestEl.querySelector('[data-mdzo-suggest-add]');
            if (first) first.focus();
            return;
          }

          if (evt.key !== 'Enter') return;
          // Das Feld liegt ausserhalb des Kontaktformulars, Enter darf hier
          // aber ohnehin nie ein Absenden ausloesen.
          evt.preventDefault();
          this.addEntry(0);
        });
      }

      if (this.suggestEl) {
        this.suggestEl.addEventListener('click', (evt) => {
          const button = evt.target.closest('[data-mdzo-suggest-add]');
          if (!button) return;
          this.addEntry(parseInt(button.dataset.index, 10) || 0);
        });

        // Klick daneben schliesst die Vorschlaege, sonst stehen sie ueber der Liste.
        document.addEventListener('click', (evt) => {
          if (this.suggestEl.hidden) return;
          if (evt.target.closest('.mdzo-angebot__sku')) return;
          this.closeSuggestions();
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }

      const counterInput = this.querySelector('[data-mdzo-counter-input]');
      const counter = this.querySelector('[data-mdzo-counter]');
      if (counterInput && counter) {
        const max = counterInput.getAttribute('maxlength');
        const update = () => { counter.textContent = `${counterInput.value.length} / ${max}`; };
        counterInput.addEventListener('input', update);
        update();
      }
    }

    /**
     * Shows the way back to the product the list was last filled from. Ohne
     * Herkunft aus dieser Sitzung bleibt der Link weg -- ein "Zurueck", das
     * irgendwohin fuehrt, ist schlechter als keines.
     */
    showOrigin() {
      const link = this.querySelector('[data-mdzo-back]');
      if (!link) return;

      let origin = null;
      try {
        const raw = window.sessionStorage.getItem(ORIGIN_KEY);
        if (raw) origin = JSON.parse(raw);
      } catch (e) {
        origin = null;
      }

      if (!origin || !origin.url || !origin.title) return;

      const label = this.querySelector('[data-mdzo-back-label]');
      if (label) {
        label.textContent = (this.dataset.backLabel || '').replace('[title]', origin.title);
      }

      link.href = origin.url;
      link.hidden = false;
    }

    /**
     * Handles remove clicks and the quantity steppers.
     * @param {Event} evt - Click event.
     */
    handleItemsClick(evt) {
      const row = evt.target.closest('[data-mdzo-item]');
      if (!row) return;
      const { variantId } = row.dataset;

      if (evt.target.closest('[data-mdzo-remove]')) {
        store.remove(variantId);
        return;
      }

      const stepper = evt.target.closest('[data-mdzo-minus], [data-mdzo-plus]');
      if (!stepper) return;

      const input = row.querySelector('[data-mdzo-qty]');
      const current = parseInt(input.value, 10) || 1;
      const next = stepper.hasAttribute('data-mdzo-plus') ? current + 1 : current - 1;

      if (next < 1) {
        store.remove(variantId);
        return;
      }

      input.value = next;
      store.setQty(variantId, next);
    }

    /**
     * @param {Event} evt - Input or change event on a quantity field.
     */
    handleQtyChange(evt) {
      const input = evt.target.closest('[data-mdzo-qty]');
      if (!input) return;

      const row = input.closest('[data-mdzo-item]');
      const value = parseInt(input.value, 10);
      if (!value || value < 1) {
        // Let the field stay empty while typing, commit on blur/change only.
        if (evt.type === 'change') {
          input.value = 1;
          store.setQty(row.dataset.variantId, 1);
        }
        return;
      }

      store.setQty(row.dataset.variantId, value);
    }

    /**
     * Switches a position onto another variant, so nobody has to delete the
     * row and add the article again from the product page.
     * @param {Event} evt - Change event on a variant select.
     */
    handleVariantSwitch(evt) {
      const select = evt.target.closest('[data-mdzo-variant]');
      if (!select) return;

      const row = select.closest('[data-mdzo-item]');
      if (!row) return;

      store.setVariant(row.dataset.variantId, select.value);
    }

    /**
     * Rebuilds the list. Rows are only recreated when the set of positions
     * changed, so typing in a quantity field does not lose focus.
     */
    async render() {
      // Jeder Tastendruck im Mengenfeld stoesst ein render an. Ohne Token
      // koennte eine langsamere fruehere Runde die neuere ueberschreiben.
      this.renderToken += 1;
      const token = this.renderToken;

      const rows = await resolveItems();
      if (token !== this.renderToken) return;

      this.rows = rows;

      const signature = rows.map((row) => row.variantId).join(',');
      const isEmpty = rows.length === 0;

      // Das Raster bleibt stehen, damit die Suche auch ohne Positionen
      // erreichbar ist. Weg sind nur Tabelle und Zusammenfassung.
      if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
      if (this.tableEl) this.tableEl.hidden = isEmpty;
      if (this.summaryEl) this.summaryEl.hidden = isEmpty;
      if (this.contentEl) this.contentEl.classList.toggle('mdzo-angebot__grid--single', isEmpty);

      if (signature !== this.signature) {
        this.signature = signature;
        this.buildRows(rows);
      } else {
        this.updateRowTotals(rows);
      }

      this.updateSummary(rows);
    }

    /**
     * @param {Array<object>} rows - Resolved rows.
     */
    buildRows(rows) {
      if (!this.itemsEl || !this.rowTemplate) return;
      this.itemsEl.textContent = '';

      rows.forEach((row) => {
        const node = this.rowTemplate.content.firstElementChild.cloneNode(true);
        node.dataset.variantId = row.variantId;

        const img = node.querySelector('[data-mdzo-img]');
        if (img) {
          if (row.image) {
            img.src = row.image;
            img.alt = row.productTitle;
          } else {
            img.remove();
          }
        }

        const hasChoice = Array.isArray(row.variants) && row.variants.length > 1;

        const title = node.querySelector('[data-mdzo-title]');
        if (title) {
          // Bei Auswahl steht die Variante im Select, im Titel waere sie doppelt.
          title.textContent = hasChoice ? row.productTitle : MdzoAngebotListe.rowName(row);
          title.href = row.url;
        }

        const variantWrap = node.querySelector('[data-mdzo-variant-wrap]');
        const variantSelect = node.querySelector('[data-mdzo-variant]');
        if (variantWrap && variantSelect) {
          if (hasChoice) {
            variantSelect.textContent = '';
            row.variants.forEach((variant) => {
              const option = document.createElement('option');
              option.value = variant.id;
              option.textContent = variant.title;
              option.selected = String(variant.id) === String(row.variantId);
              variantSelect.appendChild(option);
            });
            variantSelect.setAttribute('aria-label', this.dataset.labelVariant || 'Variante');
            variantWrap.hidden = false;
          } else {
            variantWrap.remove();
          }
        }

        const sku = node.querySelector('[data-mdzo-sku]');
        if (sku) sku.textContent = row.sku;

        const skuWrap = node.querySelector('[data-mdzo-sku-wrap]');
        if (skuWrap && !row.sku) skuWrap.hidden = true;

        const qty = node.querySelector('[data-mdzo-qty]');
        if (qty) qty.value = row.qty;

        this.itemsEl.appendChild(node);
      });

      this.updateRowTotals(rows);
    }

    /**
     * @param {Array<object>} rows - Resolved rows.
     */
    updateRowTotals(rows) {
      if (!this.itemsEl) return;

      rows.forEach((row) => {
        const node = this.itemsEl.querySelector(`[data-mdzo-item][data-variant-id="${row.variantId}"]`);
        if (!node) return;

        // Beide Betraege stehen im DOM, sichtbar ist immer nur einer. Das
        // entspricht dem Vorgehen im uebrigen Theme (siehe .price--card).
        const unitGross = node.querySelector('[data-mdzo-unit-gross]');
        if (unitGross) unitGross.textContent = formatMoney(row.grossUnit);

        const unitNet = node.querySelector('[data-mdzo-unit-net]');
        if (unitNet) unitNet.textContent = formatMoney(row.netUnit);

        const qty = node.querySelector('[data-mdzo-qty]');
        if (qty && document.activeElement !== qty) qty.value = row.qty;
      });
    }

    /**
     * @param {object} row - Resolved row.
     * @returns {string} Product name, with the variant appended when it has one.
     */
    static rowName(row) {
      return row.variantTitle && row.variantTitle !== 'Default Title'
        ? `${row.productTitle} – ${row.variantTitle}`
        : row.productTitle;
    }

    /**
     * Sums the net line totals. Summing per line keeps mixed tax rates correct
     * and follows the rule in snippets/md-net-price.liquid: never build a total
     * from rounded net unit prices.
     * @param {Array<object>} rows - Resolved rows.
     * @returns {{net: number, gross: number}} Totals in cents.
     */
    static totals(rows) {
      return rows.reduce((acc, row) => ({
        net: acc.net + row.netTotal,
        gross: acc.gross + row.grossTotal
      }), { net: 0, gross: 0 });
    }

    /**
     * Converts a net amount into the currently displayed price mode.
     *
     * Die 500-Euro-Regel ist netto definiert und wird auch netto geprueft. Im
     * Brutto-Modus aendert sich nur die angezeigte Zahl, sonst stuende neben
     * lauter Bruttopreisen ein Nettobetrag. Umgerechnet wird mit dem
     * Mischsatz der Liste, nicht mit festen 19 Prozent: Positionen koennen
     * unterschiedliche Steuersaetze haben.
     * @param {number} netCents - Net amount in cents.
     * @param {Array<object>} rows - Resolved rows.
     * @returns {number} Amount to display, in cents.
     */
    displayAmount(netCents, rows) {
      if (netLeads()) return netCents;

      const { net, gross } = MdzoAngebotListe.totals(rows);
      const factor = net > 0 ? gross / net : 1 + (this.fallbackRate / 100);
      return Math.round(netCents * factor);
    }

    /**
     * @param {Array<object>} rows - Resolved rows.
     */
    updateSummary(rows) {
      const items = rows || [];
      const { net, gross } = MdzoAngebotListe.totals(items);
      const reached = net >= this.minNet;
      const missing = Math.max(0, this.minNet - net);

      if (this.netTotalEl) this.netTotalEl.textContent = formatMoney(net);
      if (this.grossTotalEl) this.grossTotalEl.textContent = formatMoney(gross);

      const missingLabel = formatMoney(this.displayAmount(missing, items));
      const minLabel = formatMoney(this.displayAmount(this.minNet, items));

      if (this.thresholdEl) {
        this.thresholdEl.textContent = reached
          ? (this.dataset.thresholdReached || '').replace('[min]', minLabel)
          : (this.dataset.thresholdMissing || '').replace('[missing]', missingLabel);
      }

      if (this.thresholdBox) {
        this.thresholdBox.classList.toggle('mdzo-angebot__threshold--reached', reached);
        this.thresholdBox.classList.toggle('mdzo-angebot__threshold--missing', !reached);
      }

      if (this.badgeEl) this.badgeEl.hidden = !reached;

      if (this.submitBtn) {
        const blocked = !reached || items.length === 0;
        this.submitBtn.disabled = blocked;
        this.submitBtn.setAttribute('aria-disabled', String(blocked));
        this.submitBtn.classList.toggle('mdzo-angebot__submit--blocked', blocked);

        // Der Button sagt selbst, warum er nicht geht - der Hinweis darueber
        // wird beim Klickversuch leicht uebersehen.
        if (this.submitLabelEl) {
          this.submitLabelEl.textContent = blocked && items.length
            ? (this.dataset.submitBlocked || '').replace('[missing]', missingLabel)
            : this.dataset.submitLabel || '';
        }
      }
    }

    /* ------------------------------------------------------------ SKU lookup */

    /**
     * Builds the suggest endpoint from the theme routes so locale prefixes
     * such as /de/ are kept.
     * @param {string} term - Search term.
     * @returns {string} Request url.
     */
    static suggestUrl(term) {
      const base = (window.theme && theme.routes && theme.routes.predictiveSearch)
        || '/search/suggest';
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': String(SUGGEST_LIMIT),
        // Ohne "show" waeren nicht lieferbare Artikel nicht auffindbar - fuer
        // eine Angebotsanfrage ist Verfuegbarkeit aber irrelevant.
        'resources[options][unavailable_products]': 'show',
        // Mit variants.sku und variants.barcode liefert die Predictive Search
        // bei einer Artikelnummer die passende Variante gleich mit. Bei einer
        // Namenssuche bleibt variants leer, dann steht nur das Produkt da.
        'resources[options][fields]': 'variants.sku,variants.barcode,title,product_type,vendor'
      });
      return `${base}.json?${params.toString()}`;
    }

    /**
     * @param {string} message - Message to show.
     * @param {boolean} isError - Whether to style it as an error.
     */
    setSkuFeedback(message, isError) {
      if (!this.skuFeedback) return;
      this.skuFeedback.textContent = message;
      this.skuFeedback.hidden = !message;
      this.skuFeedback.classList.toggle('mdzo-angebot__sku-feedback--error', !!isError);
    }

    /**
     * Turns the suggest response into flat entries. A variant level hit keeps
     * its own id, a name hit only carries the handle and is resolved when it
     * is actually picked.
     * @param {Array<object>} products - Suggest results.
     * @returns {Array<object>} Entries for the dropdown.
     */
    static toEntries(products) {
      const entries = [];

      products.forEach((product) => {
        const variants = Array.isArray(product.variants) ? product.variants : [];

        if (variants.length) {
          variants.forEach((variant) => {
            entries.push({
              handle: product.handle,
              variantId: variant.id,
              title: product.title,
              meta: variant.title && variant.title !== 'Default Title' ? variant.title : (product.vendor || ''),
              image: variant.image || product.image || ''
            });
          });
          return;
        }

        entries.push({
          handle: product.handle,
          variantId: null,
          title: product.title,
          meta: product.vendor || '',
          image: product.image || ''
        });
      });

      return entries.slice(0, SUGGEST_LIMIT);
    }

    /**
     * Runs the debounced lookup behind the input field.
     */
    async loadSuggestions() {
      if (!this.skuInput) return;

      const term = this.skuInput.value.trim();
      if (term.length < MIN_SEARCH_CHARS) {
        this.closeSuggestions();
        this.setSkuFeedback('', false);
        return;
      }

      this.searchToken += 1;
      const token = this.searchToken;

      try {
        const response = await fetch(MdzoAngebotListe.suggestUrl(term));
        if (!response.ok) throw new Error('Suche fehlgeschlagen');

        const data = await response.json();
        if (token !== this.searchToken) return;

        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        this.renderSuggestions(MdzoAngebotListe.toEntries(products), term);
      } catch (e) {
        if (token !== this.searchToken) return;
        this.closeSuggestions();
        this.setSkuFeedback(this.dataset.skuError || '', true);
      }
    }

    /**
     * @param {Array<object>} entries - Entries to show.
     * @param {string} term - Search term, for the empty message.
     */
    renderSuggestions(entries, term) {
      this.entries = entries;

      if (!this.suggestEl || !this.suggestTemplate) return;

      if (!entries.length) {
        this.closeSuggestions();
        this.setSkuFeedback((this.dataset.skuNotFound || '').replace('[sku]', term), true);
        return;
      }

      this.setSkuFeedback('', false);
      this.suggestEl.textContent = '';

      entries.forEach((entry, index) => {
        const node = this.suggestTemplate.content.firstElementChild.cloneNode(true);
        node.querySelector('[data-mdzo-suggest-add]').dataset.index = String(index);

        const img = node.querySelector('[data-mdzo-suggest-img]');
        if (img) {
          if (entry.image) {
            img.src = entry.image;
            img.alt = '';
          } else {
            img.remove();
          }
        }

        const title = node.querySelector('[data-mdzo-suggest-title]');
        if (title) title.textContent = entry.title;

        const meta = node.querySelector('[data-mdzo-suggest-meta]');
        if (meta) meta.textContent = entry.meta;

        this.suggestEl.appendChild(node);
      });

      this.suggestEl.hidden = false;
      this.skuInput.setAttribute('aria-expanded', 'true');
    }

    closeSuggestions() {
      this.entries = [];
      if (this.suggestEl) {
        this.suggestEl.hidden = true;
        this.suggestEl.textContent = '';
      }
      if (this.skuInput) this.skuInput.setAttribute('aria-expanded', 'false');
    }

    /**
     * Resolves an entry to a concrete variant id. For a name hit the product is
     * loaded once; if the typed term happens to be an exact article number the
     * matching variant wins over the first one.
     * @param {object} entry - Suggestion entry.
     * @param {string} term - Search term.
     * @returns {Promise<string|number|null>} Variant id.
     */
    static async resolveEntry(entry, term) {
      if (entry.variantId) return entry.variantId;

      const data = await fetchProduct(entry.handle);
      if (!data.variants || !data.variants.length) return null;

      const needle = term.trim().toLowerCase();
      const exact = data.variants.find(
        (variant) => (variant.sku || '').toLowerCase() === needle
          || (variant.barcode || '').toLowerCase() === needle
      );

      return (exact || data.variants[0]).variant_id;
    }

    /**
     * Adds a suggestion, either the picked one or the first when the button is
     * used without choosing.
     * @param {number} [index] - Index in the current suggestion list.
     */
    async addEntry(index) {
      const term = this.skuInput ? this.skuInput.value.trim() : '';

      if (!term) {
        this.setSkuFeedback(this.dataset.skuEmpty || '', true);
        return;
      }

      if (!this.entries.length) {
        // Button oder Enter ohne offene Vorschlaege: erst suchen lassen.
        await this.loadSuggestions();
        if (!this.entries.length) return;
      }

      const entry = this.entries[index || 0];
      if (!entry) return;

      this.skuButton.classList.add('is-loading');
      this.setSkuFeedback(this.dataset.skuSearching || '', false);

      try {
        const variantId = await MdzoAngebotListe.resolveEntry(entry, term);
        if (!variantId) {
          this.setSkuFeedback((this.dataset.skuNotFound || '').replace('[sku]', term), true);
          return;
        }

        if (!store.add(entry.handle, variantId, 1)) {
          this.setSkuFeedback(this.dataset.skuFull || '', true);
          return;
        }

        this.skuInput.value = '';
        this.closeSuggestions();
        this.setSkuFeedback((this.dataset.skuAdded || '').replace('[title]', entry.title), false);
      } catch (e) {
        this.setSkuFeedback(this.dataset.skuError || '', true);
      } finally {
        this.skuButton.classList.remove('is-loading');
      }
    }

    /* -------------------------------------------------------------- submit */

    /**
     * Writes the positions into hidden contact fields and blocks the submit
     * below the minimum value. The form posts natively so Shopify's own spam
     * protection stays in place.
     * @param {Event} evt - Submit event.
     */
    handleSubmit(evt) {
      const { net, gross } = MdzoAngebotListe.totals(this.rows);

      if (!this.positionsEl || !this.rows.length || net < this.minNet) {
        evt.preventDefault();
        return;
      }

      const reference = MdzoAngebotListe.buildReference();
      this.setField('ref', reference);
      this.setField('net', formatMoneyWithCurrency(net));
      this.setField('gross', formatMoneyWithCurrency(gross));
      this.setField('count', String(this.rows.length));
      this.recordConsent();

      this.positionsEl.textContent = '';
      this.rows.forEach((row, index) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `contact[Position ${String(index + 1).padStart(2, '0')}]`;
        input.value = MdzoAngebotListe.describeRow(row);
        this.positionsEl.appendChild(input);
      });

      try {
        window.sessionStorage.setItem(SUBMIT_KEY, JSON.stringify({
          reference,
          net: formatMoney(net),
          netWithCurrency: formatMoneyWithCurrency(net),
          items: this.rows.map((row) => ({
            title: MdzoAngebotListe.rowName(row),
            qty: row.qty,
            image: row.image
          }))
        }));
      } catch (e) {
        // Ohne sessionStorage zeigt das Modal nur die generische Bestaetigung.
      }

      this.rescueSwallowedSubmit(evt);
    }

    /**
     * Reports whether Shopify's bot protection has bound the form. Bis dahin
     * verwirft sie jeden Absendeversuch.
     * @returns {boolean} True once a captcha provider is attached.
     */
    captchaBound() {
      if (!this.form) return false;
      return this.form.dataset.hcaptchaBound === 'true'
        || this.form.dataset.recaptchaBound === 'true';
    }

    /**
     * Catches a submit that Shopify's bot protection swallowed while its
     * captcha was still loading, and sends it again as soon as the captcha is
     * ready. Ohne das bleibt der Button beim ersten Klick wirkungslos und der
     * Nutzer bekommt keinerlei Rueckmeldung.
     *
     * Der Test laeuft erst im naechsten Tick: Shopify lauscht auf document,
     * sein Handler kommt also nach diesem hier.
     * @param {Event} evt - The submit event that was just handled.
     */
    rescueSwallowedSubmit(evt) {
      window.setTimeout(() => {
        if (!evt.defaultPrevented || !this.form) return;

        // Ist das Captcha bereits gebunden, hat etwas anderes abgebrochen.
        // Dann waere ein zweiter Versuch nur eine Endlosschleife.
        if (this.captchaBound()) return;

        this.setSubmitStatus(this.dataset.submitPending || '');
        if (this.submitBtn) this.submitBtn.classList.add('is-loading');

        // Das Laden startet nur ueber focusin oder change im Formular. Ein
        // Klick auf den Button loest das nicht in jedem Browser aus.
        const field = this.form.querySelector('input, select, textarea');
        if (field) field.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));

        const deadline = Date.now() + CAPTCHA_WAIT_MS;
        const poll = () => {
          if (this.captchaBound()) {
            this.setSubmitStatus('');
            if (this.submitBtn) this.submitBtn.classList.remove('is-loading');
            // requestSubmit statt submit: nur so laeuft der Bot-Schutz mit.
            this.form.requestSubmit(this.submitBtn || undefined);
            return;
          }

          if (Date.now() >= deadline) {
            if (this.submitBtn) this.submitBtn.classList.remove('is-loading');
            this.setSubmitStatus(this.dataset.submitRetry || '', true);
            return;
          }

          window.setTimeout(poll, CAPTCHA_POLL_MS);
        };

        window.setTimeout(poll, CAPTCHA_POLL_MS);
      }, 0);
    }

    /**
     * Shows or clears the message below the submit button.
     * @param {string} message - Text to show, empty hides the element.
     * @param {boolean} [isError] - Renders the message as a problem.
     */
    setSubmitStatus(message, isError) {
      if (!this.submitStatusEl) return;

      this.submitStatusEl.textContent = message;
      this.submitStatusEl.hidden = !message;
      this.submitStatusEl.classList.toggle('mdzo-angebot__submit-status--error', Boolean(isError));
    }

    /**
     * Documents both checkbox decisions: die verpflichtende Kenntnisnahme der
     * Datenschutzhinweise und die freiwillige Marketing-Einwilligung. Der
     * Wortlaut kommt aus dem Label selbst statt aus einem zweiten Setting --
     * so kann die Formulierung in der Mail gar nicht von der abweichen, die
     * der Nutzer gesehen hat.
     */
    recordConsent() {
      const stamp = new Date().toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

      [
        ['[data-mdzo-consent-privacy]', 'privacyNotice'],
        ['[data-mdzo-consent]', 'consent']
      ].forEach(([selector, field]) => {
        const checkbox = this.querySelector(selector);
        if (!checkbox) return;

        const label = this.querySelector(`label[for="${checkbox.id}"]`);

        this.setField(field, checkbox.checked ? 'Ja' : 'Nein');
        this.setField(`${field}At`, stamp);
        this.setField(`${field}Text`, label ? label.textContent.replace(/\s+/g, ' ').trim() : '');
      });
    }

    /**
     * @param {string} name - Field key from data-mdzo-field.
     * @param {string} value - Value to set.
     */
    setField(name, value) {
      const field = this.querySelector(`[data-mdzo-field="${name}"]`);
      if (field) field.value = value;
    }

    /**
     * @returns {string} Reference number in the form ANF-2026-09-03-0127.
     */
    static buildReference() {
      const now = new Date();
      const pad = (value) => String(value).padStart(2, '0');
      const random = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
      return `ANF-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${random}`;
    }

    /**
     * Renders one position as a compact block. The contact notification is a
     * plain field list, so everything for one article has to fit into one field
     * to stay readable.
     * @param {object} row - Resolved row.
     * @returns {string} Multi line description.
     */
    static describeRow(row) {
      return [
        MdzoAngebotListe.rowName(row),
        `Menge: ${row.qty}  |  Brutto: ${formatMoneyWithCurrency(row.grossUnit)}  |  Netto: ${formatMoneyWithCurrency(row.netUnit)}`,
        `Gesamt: ${formatMoneyWithCurrency(row.grossTotal)} brutto  /  ${formatMoneyWithCurrency(row.netTotal)} netto`,
        `SKU: ${row.sku || '-'}  |  EAN: ${row.barcode || '-'}`,
        `Produkt: ${row.absoluteUrl}`,
        `Bild: ${row.image || '-'}`,
        `IDs: product ${row.productId} / variant ${row.variantId}`
      ].join('\n');
    }

    /* ------------------------------------------------------------- success */

    /**
     * Fills and opens the success modal after the redirect, then empties the
     * list. Two signals count as "posted": the Liquid marker from
     * form.posted_successfully? and contact_posted=true, which Shopify appends
     * to the return_to url. A failed submit produces neither, so a validation
     * error never shows a confirmation.
     */
    handleSuccess() {
      const modal = this.querySelector('[data-mdzo-success]');
      if (!modal) return;

      const params = new URLSearchParams(window.location.search);
      const posted = this.querySelector('[data-mdzo-posted]') || params.get('contact_posted') === 'true';
      if (!posted) return;

      // Ohne das Flag zeigt ein Reload der Bestaetigungs-URL das Fenster erneut.
      if (params.has('contact_posted')) {
        params.delete('contact_posted');
        const query = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
      }

      let data = null;
      try {
        const raw = window.sessionStorage.getItem(SUBMIT_KEY);
        if (raw) data = JSON.parse(raw);
        window.sessionStorage.removeItem(SUBMIT_KEY);
      } catch (e) {
        data = null;
      }

      const summaryEl = modal.querySelector('[data-mdzo-success-summary]');
      if (!data) {
        // Details fehlen nur, wenn sessionStorage nicht verfuegbar war. Die
        // Bestaetigung selbst gilt trotzdem.
        if (summaryEl) summaryEl.hidden = true;
        store.clear();
        openModal(modal, this.submitBtn);
        return;
      }

      const refEl = modal.querySelector('[data-mdzo-success-ref]');
      if (refEl) refEl.textContent = data.reference;

      const listEl = modal.querySelector('[data-mdzo-success-items]');
      const itemTemplate = modal.querySelector('[data-mdzo-success-item-template]');
      if (listEl && itemTemplate && Array.isArray(data.items)) {
        listEl.textContent = '';
        data.items.forEach((item) => {
          const node = itemTemplate.content.firstElementChild.cloneNode(true);
          const img = node.querySelector('[data-mdzo-success-img]');
          if (img) {
            if (item.image) {
              img.src = item.image;
              img.alt = item.title;
            } else {
              img.remove();
            }
          }
          const title = node.querySelector('[data-mdzo-success-title]');
          if (title) title.textContent = item.title;
          const qty = node.querySelector('[data-mdzo-success-qty]');
          if (qty) qty.textContent = `${item.qty} ×`;
          listEl.appendChild(node);
        });
      }

      store.clear();
      openModal(modal, this.submitBtn);
    }
  }

  if (!customElements.get('mdzo-angebot-liste')) {
    customElements.define('mdzo-angebot-liste', MdzoAngebotListe);
  }
})();
